import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { parseOversizeRules } from '@estimator/shared';
import type {
  OversizeRulesState,
  OversizeSettings,
  UpdateOversizeSettings,
} from '@estimator/shared';
import { apiClient } from './client';

const KEY = ['oversize-settings'];

/**
 * The single oversize settings row, or null before the first save.
 *
 * The server returns null when there is no row, and Nest answers a null with
 * an empty body, which axios hands back as ''. It is normalized here so that
 * no caller parses '' as JSON and reads the rules as unreadable.
 */
export function useOversizeSettings() {
  return useQuery({
    queryKey: KEY,
    queryFn: async (): Promise<OversizeSettings | null> => {
      const { data } = await apiClient.get<OversizeSettings | ''>('/oversize-settings');
      return data || null;
    },
  });
}

/**
 * Replaces the rules whole.
 *
 * Only the settings query is invalidated: template costs are not queries. The
 * Cria Library and the Assembly Simulator derive them in a memo over
 * useTemplateCostDeps, which reads this query, so its refetch reprices both.
 */
export function useUpdateOversizeSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateOversizeSettings) => {
      const { data } = await apiClient.patch<OversizeSettings>('/oversize-settings', body);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

/**
 * The rules in the form every costing caller consumes.
 *
 * A failed query is `unreadable`, not `none`: pricing as though no rule
 * existed would show every oversized template below what Cria is quoted.
 * The rules are parsed off `data.rules` — the row itself is not a rules object
 * and would parse as unreadable.
 *
 * Memoized so the state keeps its identity between renders; buildDeps lists it
 * as a dependency and would otherwise rebuild, and reprice, on every render.
 */
export function useOversizeRulesState(): { state: OversizeRulesState; isLoading: boolean } {
  const { data, isError, isLoading } = useOversizeSettings();
  const state = useMemo<OversizeRulesState>(
    () => (isError ? { status: 'unreadable' } : parseOversizeRules(data?.rules)),
    [data, isError],
  );
  return { state, isLoading };
}
